import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';

/**
 * Coordinates the workspace views sidebar (in the shell) with the routed list table, which live in
 * separate router outlets. Provided once at the shell route so a single instance is shared across
 * the list and profile routes.
 *
 * - The shell marks the workspace `active` (so the routed list hides its own inline view tabs).
 * - The sidebar publishes the selected view's filters; the table applies them and the selection
 *   survives list↔profile navigation because this instance outlives the routed components.
 */
@Injectable()
export class ObjectWorkspaceViewFilterService {
  private readonly active$$ = signal(false);
  readonly active = this.active$$.asReadonly();

  private readonly selectedFilters$$ = new BehaviorSubject<GalaxyFilterInterface[] | null>(null);
  readonly selectedFilters$ = this.selectedFilters$$.asObservable();

  // The table publishes its currently-applied filters so the sidebar can seed a "new view" from them.
  private readonly currentFilters$$ = new BehaviorSubject<GalaxyFilterInterface[]>([]);
  readonly currentFilters$ = this.currentFilters$$.asObservable();

  setActive(active: boolean): void {
    this.active$$.set(active);
  }

  selectView(filters: GalaxyFilterInterface[]): void {
    this.selectedFilters$$.next(filters);
  }

  setCurrentFilters(filters: GalaxyFilterInterface[]): void {
    this.currentFilters$$.next(filters);
  }
}
